import {
  Button,
  ButtonLink,
  ChevronDownIcon,
  DownloadIcon,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Stack,
  TablePager,
  TextInputField,
} from '@fukuri-admins/ui/common';
import { buttonVariants } from '@fukuri-admins/ui/shared/buttonVariants';
import styles from './styles.module.scss';
import { UsersTable } from './UsersTable';

type Props = {
  /** テーブルに表示する利用者行データ */
  rows: React.ComponentProps<typeof UsersTable>['rows'];
  /**
   * アクティベーションコード関連（ログイン・アクティベーションコード列 / 同検索欄）を表示するか
   *
   * useUserList が `isPartnerFeatureFlagEnabled(flags, 'activation_code') || enableStaging` として算出し state に返す。
   * production / demo では `activation_code` PartnerFeatureFlag のみで制御され、
   * development / staging では enableStaging により PartnerFeatureFlag 未設定でも true になる。
   *
   * 電話番号列・電話番号検索欄はこのフラグに連動しない（login_method のみで決まる）
   */
  isActivationCodeEnabled: boolean;
  /**
   * 行の「編集」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onEditUser: (userId: number) => void;
  /**
   * 行の「削除」押下時のコールバック
   *
   * @param userId 操作対象の利用者 ID
   */
  onDeleteUser: (userId: number) => void;
  /**
   * 行クリック時のコールバック（利用者詳細画面への遷移）
   *
   * @param userId 操作対象の利用者 ID
   */
  onRowClick: (userId: number) => void;
  /** ページャー props */
  pagerProps: React.ComponentProps<typeof TablePager>;
  /**
   * 「メールアドレスで追加」メニュー選択時のコールバック
   *
   * isActivationCodeEnabled = true 時に表示される新規追加ドロップダウンのメニュー項目
   */
  onClickAddUserByEmail: () => void;
  /**
   * 「アクティベーションコードで追加」メニュー選択時のコールバック
   *
   * isActivationCodeEnabled = true 時に表示される新規追加ドロップダウンのメニュー項目
   */
  onClickAddUserByActivationCode: () => void;
  /** 名前検索フォームの値 */
  fullNameValue: string;
  /** メールアドレス検索フォームの値 */
  emailValue: string;
  /** 会社名検索フォームの値 */
  clientNameValue: string;
  /**
   * 電話番号検索フォームの値
   *
   * showPhoneNumberColumn=true のときのみ表示される
   */
  phoneNumberValue: string;
  /**
   * アクティベーションコード検索フォームの値
   *
   * isActivationCodeEnabled=true のときのみ表示される
   */
  activationCodeValue: string;
  /**
   * 名前検索フォーム変更時のコールバック
   *
   * @param value 入力値
   */
  onFullNameChange: (value: string) => void;
  /**
   * メールアドレス検索フォーム変更時のコールバック
   *
   * @param value 入力値
   */
  onEmailChange: (value: string) => void;
  /**
   * 会社名検索フォーム変更時のコールバック
   *
   * @param value 入力値
   */
  onClientNameChange: (value: string) => void;
  /**
   * 電話番号検索フォーム変更時のコールバック
   *
   * showPhoneNumberColumn=true のときのみ使用される
   *
   * @param value 入力値
   */
  onPhoneNumberChange: (value: string) => void;
  /**
   * アクティベーションコード検索フォーム変更時のコールバック
   *
   * isActivationCodeEnabled=true のときのみ使用される
   *
   * @param value 入力値
   */
  onActivationCodeChange: (value: string) => void;
  /** 検索ボタン押下時のコールバック */
  onSearch: () => void;
  /** 条件をリセットボタン押下時のコールバック */
  onReset: () => void;
  /**
   * アクティベーションコードのコピーボタン押下時のコールバック
   *
   * @param code コピーするアクティベーションコード
   */
  onCopyActivationCode: (code: string) => void;
  /**
   * 「電話番号」列を表示するか
   *
   * 電話番号の検索欄の表示可否も同じフラグで制御する（列と検索欄は常に一致させる）
   */
  showPhoneNumberColumn: React.ComponentProps<typeof UsersTable>['showPhoneNumberColumn'];
};

/**
 * 利用者一覧 Presentation
 *
 * - ContentsHeader にアクションボタンを配置する（title/description は Screen 層の PageHeader が担う）
 * - isActivationCodeEnabled が true のとき: ダウンロードボタン + 新規追加ドロップダウン
 * - isActivationCodeEnabled が false のとき: 追加するボタン
 * - 利用開始日・更新日時列は常時表示する
 * - 検索フォーム: 名前・メールアドレス・会社名（常時）+ 電話番号（showPhoneNumberColumn 制御）
 *   + アクティベーションコード（isActivationCodeEnabled 制御）
 * - UsersTable にテーブル本体を委譲し、TablePager を直後に配置する（gap=16 で並ぶ）
 */
export const UserListPresentation: React.FC<Props> = ({
  rows,
  isActivationCodeEnabled,
  onEditUser,
  onDeleteUser,
  onRowClick,
  pagerProps,
  onClickAddUserByEmail,
  onClickAddUserByActivationCode,
  fullNameValue,
  emailValue,
  clientNameValue,
  phoneNumberValue,
  activationCodeValue,
  onFullNameChange,
  onEmailChange,
  onClientNameChange,
  onPhoneNumberChange,
  onActivationCodeChange,
  onSearch,
  onReset,
  onCopyActivationCode,
  showPhoneNumberColumn,
}) => (
  <div data-testid='user-list'>
    <Stack direction='column' gap={16}>
      <div className={styles.actions}>
        {isActivationCodeEnabled ? (
          <Stack direction='row' gap={8}>
            <ButtonLink
              variant='outline'
              size='md'
              to='/users/activations/download'
              icon={<DownloadIcon size='xs' />}
              iconPosition='left'
            >
              利用開始コードをダウンロード
            </ButtonLink>
            <DropdownMenu>
              <DropdownMenuTrigger>
                <button type='button' className={buttonVariants({ variant: 'primary' })}>
                  新規追加
                  <ChevronDownIcon size='xs' />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem onSelect={onClickAddUserByEmail}>
                  メールアドレスで追加
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={onClickAddUserByActivationCode}>
                  利用開始コードで追加
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Stack>
        ) : (
          <ButtonLink variant='primary' size='md' to='/users/new'>
            新規追加
          </ButtonLink>
        )}
      </div>
      <Stack direction='column' gap={8}>
        <Stack direction='row' gap={8}>
          <div className={styles.searchField}>
            <TextInputField
              fieldWrapperProps={{
                htmlFor: 'user-list-full-name-input',
                labelText: '名前',
              }}
              textInput={{
                placeholder: '名前を入力',
                value: fullNameValue,
                onChange: e => onFullNameChange(e.target.value),
              }}
            />
          </div>
          <div className={styles.searchField}>
            <TextInputField
              fieldWrapperProps={{
                htmlFor: 'user-list-email-input',
                labelText: 'メールアドレス',
              }}
              textInput={{
                placeholder: 'メールアドレスを入力',
                value: emailValue,
                onChange: e => onEmailChange(e.target.value),
              }}
            />
          </div>
          <div className={styles.searchField}>
            <TextInputField
              fieldWrapperProps={{
                htmlFor: 'user-list-client-name-input',
                labelText: '会社名',
              }}
              textInput={{
                placeholder: '会社名を入力',
                value: clientNameValue,
                onChange: e => onClientNameChange(e.target.value),
              }}
            />
          </div>
          {showPhoneNumberColumn && (
            <div className={styles.searchField}>
              <TextInputField
                fieldWrapperProps={{
                  htmlFor: 'user-list-phone-number-input',
                  labelText: '電話番号',
                }}
                textInput={{
                  placeholder: '09012345678',
                  value: phoneNumberValue,
                  onChange: e => onPhoneNumberChange(e.target.value),
                }}
              />
            </div>
          )}
          {isActivationCodeEnabled && (
            <div className={styles.searchField}>
              <TextInputField
                fieldWrapperProps={{
                  htmlFor: 'user-list-activation-code-input',
                  labelText: '利用開始コード',
                }}
                textInput={{
                  placeholder: '',
                  value: activationCodeValue,
                  onChange: e => onActivationCodeChange(e.target.value),
                }}
              />
            </div>
          )}
        </Stack>
        <Stack direction='row' gap={8}>
          <Button variant='secondary' type='button' onClick={onSearch}>
            検索
          </Button>
          <Button variant='ghost' type='button' onClick={onReset}>
            条件をリセット
          </Button>
        </Stack>
      </Stack>
      <UsersTable
        rows={rows}
        isActivationCodeEnabled={isActivationCodeEnabled}
        onEditUser={onEditUser}
        onDeleteUser={onDeleteUser}
        onRowClick={onRowClick}
        onCopyActivationCode={onCopyActivationCode}
        showPhoneNumberColumn={showPhoneNumberColumn}
      />
      <TablePager {...pagerProps} />
    </Stack>
  </div>
);
